import React from "react";
import { Box, Card, Stack, Typography, Chip, IconButton, Button, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RestoreIcon from "@mui/icons-material/Restore";
import { BADGE_COLORS, getBadgeStyles } from "../../../../components/ui/badgeColors";
import DateUtils from "../../../../utils/DateUtils";

const SIN_ASIGNAR_LABEL = "Sin asignar";
const ESTADO_ACTIVO = "Activo";
const ESTADO_DE_BAJA = "De baja";

/**
 * Vista mobile con tarjetas de usuarios
 *
 * Recibe los mismos callbacks que getUsuariosColumns (onEditar, onDarBaja,
 * onRestaurar, idOperacionEnCurso), con la fila completa como argumento, para
 * que la página (TASK-006) pase exactamente las mismas props al DataGrid y a
 * esta lista sin adaptadores.
 */
const UsuariosMobileList = ({
    usuarios,
    loading,
    incluirBajas,
    onEditar,
    onDarBaja,
    onRestaurar,
    idOperacionEnCurso = null,
}) => {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!usuarios?.length) {
        // DECISION: con incluirBajas activo la lista trae activos Y de baja, así
        // que "No hay usuarios dados de baja" sería falso si simplemente no hay
        // ningún usuario. "No hay usuarios activos" es la lectura correcta con
        // el filtro desactivado (puede haber usuarios, todos de baja); con el
        // filtro activo el conjunto es el total, así que su ausencia sí es "no
        // hay usuarios registrados".
        const mensajeVacio = incluirBajas ? "No hay usuarios registrados" : "No hay usuarios activos";

        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "#6c757d", fontSize: 14 }}>{mensajeVacio}</Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {usuarios.map((usuario) => (
                <UsuarioCard
                    key={usuario.id}
                    usuario={usuario}
                    disabled={idOperacionEnCurso === usuario.id}
                    onEditar={onEditar}
                    onDarBaja={onDarBaja}
                    onRestaurar={onRestaurar}
                />
            ))}
        </Stack>
    );
};

/**
 * Tarjeta individual de usuario: datos + acciones. Extraída para mantener el
 * componente principal legible y evitar recrear JSX inline en el map.
 */
const UsuarioCard = ({ usuario, disabled, onEditar, onDarBaja, onRestaurar }) => {
    const deBaja = Boolean(usuario.fechaBaja);
    const colorEstado = deBaja ? BADGE_COLORS.GRAY : BADGE_COLORS.GREEN;
    const nombreRol = usuario.rol?.nombre ?? null;

    return (
        <Card sx={{ p: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Stack spacing={1.5}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#212529" }}>
                            {usuario.username}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#495057" }}>{usuario.email}</Typography>
                    </Box>
                    <Chip
                        label={deBaja ? ESTADO_DE_BAJA : ESTADO_ACTIVO}
                        size="small"
                        sx={getBadgeStyles(colorEstado)}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>Rol:</Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: nombreRol ? 600 : 400,
                            color: nombreRol ? "#495057" : "#6c757d",
                        }}
                    >
                        {nombreRol || SIN_ASIGNAR_LABEL}
                    </Typography>
                </Box>

                {deBaja && (
                    <Typography sx={{ fontSize: 11, color: "#6c757d", textAlign: "right" }}>
                        Desde {DateUtils.formatearFecha(usuario.fechaBaja)}
                    </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => onEditar(usuario)}
                        disabled={disabled}
                        aria-label="Editar usuario"
                        sx={{ color: "#cc071e" }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    {deBaja ? (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestoreIcon fontSize="small" />}
                            onClick={() => onRestaurar(usuario)}
                            disabled={disabled}
                        >
                            Restaurar
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<PersonOffIcon fontSize="small" />}
                            onClick={() => onDarBaja(usuario)}
                            disabled={disabled}
                            sx={{ color: "#6c757d", borderColor: "#ced4da" }}
                        >
                            Dar de baja
                        </Button>
                    )}
                </Box>
            </Stack>
        </Card>
    );
};

export default UsuariosMobileList;
